/**
 * The rest of the question "which repository?" — which branch of it, and which
 * folder inside it.
 *
 * These two used to live on the configure step, behind the App card's Edit.
 * That put them AFTER the detection: we read the default branch at the
 * repository root, filled the form from what we found there, and only then
 * offered to change the two inputs that decide what we should have read. For a
 * monorepo — the case where the folder matters at all — every value on the
 * step arrived wrong, and fixing them re-ran the whole thing.
 *
 * Asked here, they are answered before anything is read: the detection's first
 * attempt is at the right branch and the right folder. They stay editable on
 * the configure step, where the App card still holds both.
 *
 * The branch list is a convenience, never a gate. `BranchSelect` is a
 * combobox: a repository whose branches fail to load, or whose branch is past
 * the one-page cap, is still reachable by typing — and Continue is never
 * blocked on a listing that we could not fetch.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { GitHubRepository } from '@back4app2/sdk';
import back4app2 from '../../back4app2';
import BranchSelect from '../BranchSelect/BranchSelect';
import RepositoryBrowser from '../RepositoryBrowser/RepositoryBrowser';
import TextInput from '../FormInputField/TextInput/TextInput';
import { DsButton } from '../ds';
import { FolderIcon } from '../ds/icons';

const ConfirmRepository = ({ repository, onBack, onContinue }: {
  repository: GitHubRepository,
  /** Closes the drawer, leaving the list as it was. */
  onBack: () => void,
  onContinue: (branchName: string, rootDirectoryPath: string) => void,
}) => {
  const [branchName, setBranchName] = useState(repository.defaultBranch || '');
  const [rootDirectoryPath, setRootDirectoryPath] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isBrowsing, setIsBrowsing] = useState(false);
  // Only the newest listing may write: switching repositories quickly would
  // otherwise let a slow first response land on top of the right one.
  const requestRef = useRef(0);

  const loadBranches = useCallback(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    setIsLoadingBranches(true);

    back4app2
      .repositoryBranches(repository.id)
      .then(result => {
        if (requestRef.current === requestId) {
          setBranches(result);
        }
      })
      .catch(e => {
        // Not surfaced: the field still takes a typed branch, and an error on
        // a listing nobody asked for would read as the repository being
        // broken.
        console.error('error listing repository branches', e);

        if (requestRef.current === requestId) {
          setBranches([]);
        }
      })
      .finally(() => {
        if (requestRef.current === requestId) {
          setIsLoadingBranches(false);
        }
      });
  }, [repository.id]);

  useEffect(() => { loadBranches(); }, [loadBranches]);

  return <>
    {/* Reads the branch that is selected right now: browsing before choosing
        a branch would list the default one's folders and hand back a path
        that may not exist on the branch being built. */}
    {isBrowsing && (
      <RepositoryBrowser
        open={isBrowsing}
        repositoryId={repository.id}
        branchName={branchName}
        initialPath={rootDirectoryPath}
        onClose={() => setIsBrowsing(false)}
        // It closes itself after selecting — `onSelect` only has to take the
        // path.
        onSelect={setRootDirectoryPath}
      />
    )}

    {/* A drawer under the row that opened it, not a screen of its own. The
        repository's name is on that row, so this repeats neither the name nor
        a way back to a list it never replaced — closing it is the same button
        that opened it. */}
    <div className="px-4 lg:px-5 pb-5 pt-4 flex flex-col gap-4 border-t border-[rgba(32,138,236,0.18)] bg-[rgba(32,138,236,0.055)] shadow-[inset_0_1px_0_rgba(255,255,255,.035)]">
      <div className="grid gap-4 lg:grid-cols-2">
        {/* No label of its own: BranchSelect draws one, and two of them
            stacked read as two fields. */}
        <div className="flex flex-col">
          <BranchSelect
            value={branchName}
            branches={branches}
            onChange={setBranchName}
            onRefresh={loadBranches}
            isLoading={isLoadingBranches}
            compact
          />
        </div>

        {/* Typed OR browsed, exactly as the configure step has always offered
            it. A path in a monorepo is three levels of names nobody recalls
            exactly, and this control is now the FIRST place it is asked for —
            leaving only the text box here would have moved the question
            earlier and taken away the thing that answers it.

            Browse is always offered, whatever the repository looks like: we
            only learn that a path has no subfolders after reading it, so a
            button that greyed itself out would do so a second late, and an
            empty listing is also what a mistyped path produces — exactly when
            the browser is the way out. */}
        <TextInput
          value={rootDirectoryPath}
          label="Root directory"
          name="rootDirPath"
          onChange={event => setRootDirectoryPath(event.target.value)}
          placeholderText="./"
          infoText="Defaults to the repository root. Builds run inside this folder only."
          labelClassName="!text-ds-115"
          className="lg:!h-9 !bg-navy-900/20 !border-navy-500/60 hover:!border-navy-400/80 focus-within:!border-action/60 focus-within:!shadow-none"
          inputClassName="!text-ds-115 lg:!text-ds-115"
          hintClassName="!text-ds-105 hidden sm:block"
          endAdornment={
            <button
              type="button"
              onClick={() => setIsBrowsing(true)}
              className="b4-focus inline-flex items-center gap-1.5 whitespace-nowrap rounded-control px-2 py-1 !text-ds-105 text-navy-200 hover:text-white transition-colors duration-[120ms]"
            >
              <FolderIcon size={12} /> Browse folders
            </button>
          }
        />
      </div>

      <div className="flex justify-end gap-2">
        <DsButton
          variant="ghost"
          size="md"
          className="!h-7 !px-3 !text-ds-115"
          onClick={onBack}
        >
          Cancel
        </DsButton>
        {/* Never disabled on the branch being empty: a repository with no
            commits has no default branch to offer, and the configure step
            asks for one again before anything is created. */}
        <DsButton
          variant="action"
          size="md"
          className="!h-7 !px-3 !text-ds-115"
          onClick={() => onContinue(branchName.trim(), rootDirectoryPath.trim())}
        >
          Continue
        </DsButton>
      </div>
    </div>
  </>;
};

export default ConfirmRepository;
